import { cn } from "@/utils/cn"
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react"
import { CommandGroup, CommandInput, CommandItem, CommandList } from "./command"
import { Skeleton } from "./skeleton"
import { Check } from "lucide-react"
import { Command as CommandPrimitive } from "cmdk"
import { useFormikContext } from "formik"
import { Label } from "./label"
import { FieldErrorMessage, useFieldError } from "./field-error"
import { Col } from "../grid/col"
import { AxiosPromise } from "axios"
import debounce from "../utils/Debounce"
import { Cross1Icon, Cross2Icon, ReloadIcon } from "@radix-ui/react-icons"

export type Option = Record<"value" | "label", string> & Record<string, string>

type AutoCompleteProps = {
  emptyMessage?: string
  value?: Option
  onValueChange?: (value: any) => void
  disabled?: boolean
  placeholder?: string
  onSearch(params: string): AxiosPromise<any>;
  getOptionLabel(value: any): string;
  getOptionValue(value: any): string;
  getKey(value: any): string;
  error?: string;
}

export const AutoComplete = ({
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  onSearch,
  getOptionLabel,
  getOptionValue, error, getKey
}: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const [options, setOptions] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>();
  const [isOpen, setOpen] = useState(false)
  const [selected, setSelected] = useState<any>();
  const [inputValue, setInputValue] = useState<string>("")

  useEffect(() => {
    setSelected(getOptionValue(value))
    value && setInputValue(getOptionLabel(value));
  }, [value])

  const getIcon = () => {
    if (loading){
        return (
            <div className={`absolute grid w-5 h-5 place-items-center text-muted-foreground top-2/4 right-3 -translate-y-2/4`}>
                <ReloadIcon className="ml-2 animate-spin"/>
            </div>
        )
    } else if (selected){
      return (
        <div className={`absolute grid w-5 h-5 place-items-center text-muted-foreground top-2/4 right-3 -translate-y-2/4`} title="Limpar" 
          onClick={clear}>
            <Cross2Icon className="ml-2 cursor-pointer"/>
        </div>
    )
    }
  }
  
  const TIME_WAIT = 600;
  var smartSearch: (params: string) => Promise<any> = debounce(onSearch, TIME_WAIT);
  
  const loadOptions = async (search: string) => {
    try{
        setLoading(true);
        setInputValue(search);
        var response = await smartSearch(search);

        const data = response.data.content
            ? response.data.content
            : response.data;
        
        setOptions(data);
        setOpen(true);
    }catch(error){
        console.error(error);
    } finally {
        setLoading(false);
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current
      if (!input) {
        return
      }

      // Keep the options displayed when the user is typing
      if (!isOpen) {
        setOpen(true)
      }

      // This is not a default behaviour of the <input /> field
      if (event.key === "Enter" && input.value !== "") {
        const optionToSelect = options.find(
          (option: any) => getOptionLabel(option) === input.value,
        )
        if (optionToSelect) {
          setSelected(optionToSelect)
          onValueChange?.(getOptionValue(optionToSelect))
        }
      }

      if (event.key === "Escape") {
        input.blur()
      }
    },
    [isOpen, options, onValueChange],
  )

  const handleBlur = useCallback(() => {
    setOpen(false)
    selected && setInputValue(getOptionLabel(selected))
  }, [selected])

  const clear = () => {
    setInputValue('');
    setSelected(undefined);
    onValueChange?.(undefined);
  }

  const handleSelectOption = useCallback(
    (selectedOption: any) => {
      selectedOption && setInputValue(getOptionLabel(selectedOption))

      setSelected(selectedOption)
      onValueChange?.(getOptionValue(selectedOption))

      // This is a hack to prevent the input from being focused after the user selects an option
      // We can call this hack: "The next tick"
      setTimeout(() => {
        inputRef?.current?.blur()
      }, 0)
    },
    [onValueChange],
  )

  return (
    <CommandPrimitive onKeyDown={handleKeyDown} aria-invalid={!!error} className={cn("rounded-md border border-input bg-transparent h-9 focus-within:ring-1 focus-within:ring-ring shadow-sm", error && "border-destructive focus-within:ring-destructive")}>
      <div className="relative">
        {getIcon()}
        <CommandInput
          ref={inputRef}
          value={inputValue}
          onValueChange={(search: string) => loadOptions(search)}
          onBlur={handleBlur}
          onFocus={() => loadOptions(inputValue)}
          placeholder={placeholder}
          disabled={disabled}
          className={`text-sm`}
          data-cmdk-input-wrapper='border-0'
        />
      </div>
      <div className="relative">
        <div
          className={cn(
            "animate-in fade-in-0 zoom-in-95 absolute top-0 z-30 w-full bg-background outline-none border border-input shadow",
            isOpen ? "block" : "hidden",
          )}
        >
          <CommandList className="rounded-md">
            {options.length > 0 && !loading ? (
              <CommandGroup>
                {options.map((option: any) => {
                  const isSelected = selected && getKey(selected) === getKey(option)
                  return (
                    <CommandItem
                      key={getOptionValue(option)}
                      value={getOptionLabel(option)}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                      }}
                      onSelect={() => handleSelectOption(option)}
                      className={cn(
                        "flex w-full items-center gap-2 cursor-pointer",
                        !isSelected ? "pl-8" : null,
                      )}
                    >
                      {isSelected ? <Check className="w-4" /> : null}
                      {getOptionLabel(option)}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            ) : null}
            {!loading ? (
              <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                {emptyMessage||'Nenhum registro encontrado.'}
              </CommandPrimitive.Empty>
            ) : null}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  )
}

interface FormikAutoCompleteProps extends AutoCompleteProps {
  field?: any;
  name: string;
  label?: string;
  size?: number;
}

export const FormikAutoComplete = (props: FormikAutoCompleteProps) => {
    const formikProps = useFormikContext<any>();
  const {values, setFieldValue, setFieldTouched, handleChange, handleBlur} = formikProps;
    const name = props.field?.name || props.name;
    const errorMessage = useFieldError(name);

    var component = (
        <>
        {props.label && (
            <Label>{props.label}</Label>
        )}
        <AutoComplete
            {...props}
            error={errorMessage}
            onValueChange={(value: Option) => {
              setFieldValue(name, value ? props.getOptionValue!(value) : undefined);
              props.onValueChange && props.onValueChange(value);
            }}
            value={values[name]}
        />
        <FieldErrorMessage message={errorMessage} />
        </>
    );

    if (props.size){
        return (
          <Col xs={12} sm={props.size}>{component}</Col>
        )
      }

    return component;
}